import React, { useState } from 'react';
import '../styles/dashboard-pages.css';
import './DocumentationPage.css';

interface Section {
  id: string;
  title: string;
  icon: string;
  content: React.ReactNode;
}

const DocumentationPage: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('overview');

  const sections: Section[] = [
    {
      id: 'overview',
      title: 'Overview',
      icon: '🏠',
      content: (
        <div className="doc-section-content">
          <h3>Welcome to Neural Viz</h3>
          <p>
            Neural Viz is a comprehensive platform for building, training, and managing neural networks. 
            Our platform provides an intuitive interface for both beginners and experts to create powerful 
            machine learning models without extensive coding knowledge.
          </p>
          
          <div className="feature-grid">
            <div className="feature-card">
              <div className="feature-icon">🎓</div>
              <h4>Training</h4>
              <p>Train models from scratch or use pre-configured datasets with our easy-to-use training interface.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h4>Data Management</h4>
              <p>Upload, organize, and process your data with powerful tools for cleaning and preparation.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🤖</div>
              <h4>Model Management</h4>
              <p>Store, version, and deploy your trained models with comprehensive tracking and monitoring.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">⚙️</div>
              <h4>Model Builder</h4>
              <p>Create complex ML models visually with our 3D drag-and-drop interface.</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: '🚀',
      content: (
        <div className="doc-section-content">
          <h3>Quick Start Guide</h3>
          
          <div className="step-list">
            <div className="step-item">
              <div className="step-number">1</div>
              <div className="step-content">
                <h4>Create an Account</h4>
                <p>Sign up for a free account to get started. You'll have access to all basic features immediately.</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">2</div>
              <div className="step-content">
                <h4>Upload Your Data</h4>
                <p>Navigate to the <strong>Data</strong> tab and upload your training data. Supported formats include CSV, JSON, images, and more.</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">3</div>
              <div className="step-content">
                <h4>Configure Your Dataset</h4>
                <p>Once uploaded, configure your data in the <strong>Datasets</strong> tab to make it ready for training.</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">4</div>
              <div className="step-content">
                <h4>Start Training</h4>
                <p>Go to <strong>Training</strong> and choose either "New Training" to start from scratch or "Quick Train" to use a pre-configured dataset.</p>
              </div>
            </div>
            
            <div className="step-item">
              <div className="step-number">5</div>
              <div className="step-content">
                <h4>Monitor Progress</h4>
                <p>Check the <strong>Processes</strong> tab to monitor your training jobs in real-time.</p>
              </div>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'training',
      title: 'Training',
      icon: '🎓',
      content: (
        <div className="doc-section-content">
          <h3>Training Your Models</h3>
          
          <div className="info-box">
            <h4>New Training</h4>
            <p>
              Use "New Training" when you want to start completely from scratch. This option allows you to:
            </p>
            <ul>
              <li>Upload input and output files</li>
              <li>Provide example files for the model to learn from</li>
              <li>Configure custom training parameters</li>
              <li>Use GPT-powered model generation</li>
            </ul>
          </div>
          
          <div className="info-box">
            <h4>Quick Train</h4>
            <p>
              "Quick Train" is perfect when you have a pre-configured dataset ready to use. Simply:
            </p>
            <ul>
              <li>Select from your configured datasets</li>
              <li>Set training parameters (epochs, batch size, learning rate)</li>
              <li>Start training immediately</li>
            </ul>
          </div>
          
          <div className="info-box tip">
            <strong>💡 Tip:</strong> Pre-configured datasets in the Datasets tab are already validated and formatted, 
            making them ideal for Quick Train.
          </div>
        </div>
      ),
    },
    {
      id: 'data',
      title: 'Data Management',
      icon: '📊',
      content: (
        <div className="doc-section-content">
          <h3>Managing Your Data</h3>
          
          <h4>Data vs Datasets</h4>
          <div className="comparison-box">
            <div className="comparison-item">
              <h5>📁 Data</h5>
              <p>Raw uploaded files in any format. These need to be processed and configured before use.</p>
              <ul>
                <li>Upload any file format</li>
                <li>Process and clean your data</li>
                <li>Configure as a dataset when ready</li>
              </ul>
            </div>
            <div className="comparison-item">
              <h5>📊 Datasets</h5>
              <p>Pre-configured data collections that are validated and ready for direct use in training.</p>
              <ul>
                <li>Platform-compatible format</li>
                <li>Validated and structured</li>
                <li>Ready for immediate training</li>
              </ul>
            </div>
          </div>
          
          <h4>Data Processing Tools</h4>
          <div className="tool-grid">
            <div className="tool-card">
              <div className="tool-icon">⚙️</div>
              <h5>Data Cleaning</h5>
              <p>Remove duplicates, handle missing values, and clean your data.</p>
            </div>
            <div className="tool-card">
              <div className="tool-icon">📊</div>
              <h5>Normalization</h5>
              <p>Scale and normalize your data for better model performance.</p>
            </div>
            <div className="tool-card">
              <div className="tool-icon">🔄</div>
              <h5>Augmentation</h5>
              <p>Generate synthetic variations to expand your dataset.</p>
            </div>
            <div className="tool-card">
              <div className="tool-icon">✂️</div>
              <h5>Splitting</h5>
              <p>Split data into train, validation, and test sets.</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'processes',
      title: 'Processes',
      icon: '⚙️',
      content: (
        <div className="doc-section-content">
          <h3>Monitoring Training Processes</h3>
          
          <p>
            The Processes tab shows all your active and completed training jobs. You can monitor:
          </p>
          
          <div className="process-features">
            <div className="process-feature">
              <h4>📊 Real-time Progress</h4>
              <p>Watch your training progress with live updates on epoch completion, loss values, and accuracy metrics.</p>
            </div>
            
            <div className="process-feature">
              <h4>⏸️ Control Actions</h4>
              <p>Pause, resume, or stop training jobs as needed. Completed jobs can be viewed for detailed results.</p>
            </div>
            
            <div className="process-feature">
              <h4>📈 Status Tracking</h4>
              <p>See at a glance which processes are running, completed, paused, or failed with color-coded status indicators.</p>
            </div>
            
            <div className="process-feature">
              <h4>🔍 Detailed Information</h4>
              <p>View comprehensive details including dataset used, model architecture, training parameters, and timestamps.</p>
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'models',
      title: 'Models',
      icon: '🤖',
      content: (
        <div className="doc-section-content">
          <h3>Model Management</h3>
          
          <p>
            After training, your models are automatically saved and can be managed in the Models section.
          </p>
          
          <div className="model-features">
            <h4>Model Features</h4>
            <ul>
              <li><strong>Version Control:</strong> Track different versions of your models</li>
              <li><strong>Performance Metrics:</strong> View training history, accuracy, and loss curves</li>
              <li><strong>Deployment:</strong> Deploy models for inference via API</li>
              <li><strong>Export:</strong> Download models in various formats (PyTorch, ONNX, etc.)</li>
              <li><strong>Sharing:</strong> Share models with team members or make them public</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'api',
      title: 'API',
      icon: '🔌',
      content: (
        <div className="doc-section-content">
          <h3>API Documentation</h3>
          
          <p>
            Access your models programmatically through our RESTful API. Generate API keys in the API section 
            to authenticate your requests.
          </p>
          
          <div className="api-example">
            <h4>Example API Call</h4>
            <pre className="code-block">
{`POST /api/v1/models/{model_id}/predict
Headers:
  Authorization: Bearer YOUR_API_KEY
  Content-Type: application/json

Body:
{
  "input": "your input data"
}

Response:
{
  "prediction": "model output",
  "confidence": 0.95
}`}
            </pre>
          </div>
          
          <div className="info-box">
            <h4>API Endpoints</h4>
            <ul>
              <li><code>GET /api/v1/models</code> - List all your models</li>
              <li><code>POST /api/v1/models/{'{id}'}/predict</code> - Make predictions</li>
              <li><code>GET /api/v1/models/{'{id}'}/metrics</code> - Get model metrics</li>
              <li><code>POST /api/v1/training/start</code> - Start a training job</li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      id: 'faq',
      title: 'FAQ',
      icon: '❓',
      content: (
        <div className="doc-section-content">
          <h3>Frequently Asked Questions</h3>
          
          <div className="faq-list">
            <div className="faq-item">
              <h4>What file formats are supported?</h4>
              <p>
                We support CSV, JSON, Parquet, Excel, HDF5, and common image formats (JPG, PNG, etc.). 
                For text data, we support TXT, CSV, and JSON formats.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>How much data can I upload?</h4>
              <p>
                Free accounts have a 500 MB limit. Premium plans offer significantly more storage. 
                Check the Billing section for plan details.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>Can I train multiple models simultaneously?</h4>
              <p>
                Yes! You can run multiple training jobs at the same time. Monitor them all in the Processes tab.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>How do I export my trained model?</h4>
              <p>
                Go to the Models section, select your model, and click the export button. 
                You can choose from various formats including PyTorch (.pt), ONNX, and TensorFlow formats.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>What's the difference between Data and Datasets?</h4>
              <p>
                <strong>Data</strong> refers to raw uploaded files that may need processing. 
                <strong>Datasets</strong> are pre-configured, validated collections that are ready for immediate use in training.
              </p>
            </div>
            
            <div className="faq-item">
              <h4>How do I get support?</h4>
              <p>
                You can reach out through the Settings page or email support@neuralviz.com. 
                We typically respond within 24 hours.
              </p>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const activeSectionData = sections.find(s => s.id === activeSection) || sections[0];

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <h1>Documentation</h1>
        <p>Learn how to use Neural Viz effectively</p>
      </div>

      <div className="documentation-container">
        <div className="doc-sidebar">
          <nav className="doc-nav">
            {sections.map((section) => (
              <button
                key={section.id}
                className={`doc-nav-item ${activeSection === section.id ? 'active' : ''}`}
                onClick={() => setActiveSection(section.id)}
              >
                <span className="doc-nav-icon">{section.icon}</span>
                <span className="doc-nav-label">{section.title}</span>
              </button>
            ))}
          </nav>
        </div>

        <div className="doc-content">
          <div className="doc-section">
            <div className="doc-section-header">
              <span className="doc-section-icon">{activeSectionData.icon}</span>
              <h2>{activeSectionData.title}</h2>
            </div>
            {activeSectionData.content}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentationPage;

